import React, { useState } from 'react';
import { ArrowButtonHome } from '../sideBySide/LivePreview';
import { BannerData } from '../../BannerLandscapePage';
import { DesktopIcon } from '../../../../assets/DesktopIcon';
import { MobileIcon } from '../../../../assets/MobileIcon';

interface BannerLivePreviewProps {
    bannerData: BannerData;
    linkMode: 'banner' | 'button';
}

const BannerLivePreview: React.FC<BannerLivePreviewProps> = ({ bannerData, linkMode }) => {
    const [viewMode, setViewMode] = useState<'desktop' | 'mobile'>('desktop');
    const isMobileView = viewMode === 'mobile';

    const alignMap: Record<string, string> = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end'
    };

    const textFlexAlign = alignMap[bannerData.textAlignment] || 'flex-start';
    const btnFlexAlign = alignMap[bannerData.buttonAlignment] || 'flex-start';

    const desktopSrc = bannerData.desktopSrc;
    const mobileSrc = bannerData.mobileSrc;
    const bgImgSrc = isMobileView ? (mobileSrc || desktopSrc) : (desktopSrc || mobileSrc);
    const hasBgImage = Boolean(bgImgSrc);

    const showButton = linkMode === 'button' && Boolean(bannerData.buttonText);
    const showBannerLink = linkMode === 'banner' && Boolean(bannerData.url);

    const containerClassName = `smallBannerHome__container layout-${bannerData.layout} ${isMobileView ? 'is-mobile' : ''}`;
    const containerStyle = {
        backgroundColor: bannerData.bannerBackgroundColor,
        '--banner-brightness': `${bannerData.imageBrightness ?? 100}%`,
    } as React.CSSProperties;

    const bgImage = hasBgImage ? (
        <picture className="smallBannerHome__bgPicture" aria-hidden>
            <img
                className="smallBannerHome__bgImg"
                src={bgImgSrc!}
                alt=""
            />
        </picture>
    ) : null;

    const bannerInner = (
        <>
            <div
                className="smallBannerHome__textContainer"
                style={{
                    alignItems: textFlexAlign,
                    textAlign: bannerData.textAlignment as React.CSSProperties['textAlign'],
                }}
            >
                {bannerData.primaryText && (
                    <p className="smallBannerHome__title" style={{ color: bannerData.primaryTextColor }}>
                        {bannerData.primaryText}
                    </p>
                )}
                {bannerData.secondaryText && (
                    <p className="smallBannerHome__subtitle" style={{ color: bannerData.secondaryTextColor }}>
                        {bannerData.secondaryText}
                    </p>
                )}
            </div>

            {showButton && (
                <div
                    className="smallBannerHome__btnWrapper"
                    style={{ justifyContent: btnFlexAlign }}
                >
                    <a
                        className="smallBannerHome__btn"
                        href={bannerData.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.preventDefault()}
                    >
                        <button style={{ backgroundColor: bannerData.buttonColor, color: bannerData.buttonTextColor }}>
                            {bannerData.buttonText}
                            <ArrowButtonHome />
                        </button>
                    </a>
                </div>
            )}
        </>
    );

    return (
        <div className="banner-preview-container live-preview-section mt-4">
            <div className="preview-header">
                <h3> LIVE PREVIEW </h3>
                <div className="view-mode-toggle">
                    <button
                        type="button"
                        onClick={() => setViewMode('desktop')}
                        className={viewMode === 'desktop' ? 'active' : ''}
                    >
                        <DesktopIcon width={16} height={16} fill={viewMode === 'desktop' ? '#5493DA' : '#64748b'} /> Desktop
                    </button>
                    <button
                        type="button"
                        onClick={() => setViewMode('mobile')}
                        className={viewMode === 'mobile' ? 'active' : ''}
                    >
                        <MobileIcon width={16} height={16} fill={viewMode === 'mobile' ? '#5493DA' : '#64748b'} /> Mobile
                    </button>
                </div>
            </div>

            {showBannerLink ? (
                <a
                    className={containerClassName}
                    style={containerStyle}
                    href={bannerData.url || '#'}
                    onClick={(e) => e.preventDefault()}
                >
                    {bgImage}
                    <div className="smallBannerHome__content">
                        {bannerInner}
                    </div>
                </a>
            ) : (
                <div className={containerClassName} style={containerStyle}>
                    {bgImage}
                    <div className="smallBannerHome__content">
                        {bannerInner}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BannerLivePreview;